import {
  CampaignPrefillResponse,
  VacancyTextVariant,
  VonqTaxonomyItem,
} from "../types/campaignPrefill";

type FormPair = { id: number; name: string };

function toFormPair(
  value: { id?: number; vonqId?: number; name?: string; description?: string } | null | undefined
): FormPair | null {
  if (!value) return null;
  const id = value.id ?? value.vonqId;
  const name = value.name ?? value.description;
  if (id == null || !name) return null;
  return { id: Number(id), name: String(name) };
}

function resolveFromVonqMapping(
  mappingLink: unknown,
  suggestions?: VonqTaxonomyItem[]
): FormPair | null {
  const fromMapping = toFormPair(
    mappingLink as {
      id?: number;
      vonqId?: number;
      name?: string;
      description?: string;
    }
  );
  if (fromMapping) return fromMapping;

  const first = suggestions?.[0];
  if (!first?.vonqId || !first.description) return null;
  return { id: first.vonqId, name: first.description };
}

export function buildWizardDataFromPrefill(
  prefill: CampaignPrefillResponse,
  jobSpecificationId: number
) {
  const cp = prefill.campaignPrefill;
  const vm = prefill.vonqMapping || {};
  const suggestions = prefill.suggestions || {};

  const jobTitle = resolveFromVonqMapping(vm.jobTitle, suggestions.jobTitle);
  const jobFunction =
    resolveFromVonqMapping(vm.jobFunction, suggestions.jobFunction) ||
    resolveFromVonqMapping(vm.jobCategory, suggestions.jobFunction);
  const location = resolveFromVonqMapping(vm.location, suggestions.location);
  const industry = resolveFromVonqMapping(vm.industry, suggestions.industry);
  const education = resolveFromVonqMapping(
    vm.educationLevel,
    suggestions.educationLevel
  );
  const seniority = resolveFromVonqMapping(vm.seniority, suggestions.seniority);
  const form3JobFunction = jobFunction;
  const form3Industry = industry;

  const f4 = cp.form4 || {};
  const campaignData = (cp.form5?.campaignData || {}) as Record<string, unknown>;
  const workingLocation = (cp.form5?.workingLocation || {}) as Record<string, unknown>;

  const vacancyVariants =
    cp.vacancyText?.variants ||
    (prefill.vacancyText
      ? {
          professional: prefill.vacancyText.professional,
          neutral: prefill.vacancyText.neutral,
          engaging: prefill.vacancyText.engaging,
        }
      : null);

  const defaultVariant: VacancyTextVariant = "professional";
  const defaultDescription =
    vacancyVariants?.[defaultVariant] || "";

  const vonqJobTitleName = jobTitle?.name || "";

  const weekly = (vm.weeklyWorkingHours || {}) as {
    from?: number;
    to?: number;
  };
  const resolvedFromHours =
    campaignData.fromWorkingHours != null &&
    String(campaignData.fromWorkingHours).trim() !== ""
      ? String(campaignData.fromWorkingHours)
      : weekly.from != null
        ? String(weekly.from)
        : "";
  const resolvedToHours =
    campaignData.toWorkingHours != null &&
    String(campaignData.toWorkingHours).trim() !== ""
      ? String(campaignData.toWorkingHours)
      : weekly.to != null
        ? String(weekly.to)
        : "";

  return {
    jobSpecificationId,
    jobVacancyTextId:
      cp.vacancyText?.jobVacancyTextId ?? prefill.vacancyText?.id ?? null,
    vacancyTextVariants: vacancyVariants,
    vacancyTextVariant: defaultVariant as VacancyTextVariant,
    _prefillLoaded: true,
    _prefillFetchedAt: new Date().toISOString(),
    _prefillGaps: prefill.gaps || [],
    Form1Data: {
      jobTitleSearchTerm: vonqJobTitleName,
      locationSearchTerm: location?.name || "",
      jobTitle,
      jobFunction,
      location,
      industries: industry,
    },
    Form3Data: {
      education,
      seniority,
      jobFunction: form3JobFunction,
      industry: form3Industry,
    },
    Form4Data: {
      recruiterFullName: f4.recruiterFullName || prefill.user?.name || "",
      recruiterEmail: f4.recruiterEmail || prefill.user?.email || "",
      contactFullName:
        f4.contactFullName || f4.recruiterFullName || prefill.user?.name || "",
      contactEmail:
        f4.contactEmail || f4.recruiterEmail || prefill.user?.email || "",
      contactPhone: f4.contactPhone || "",
    },
    Form5Data: {
      campaignData: {
        ...campaignData,
        jobTitle: vonqJobTitleName || String(campaignData.jobTitle || ""),
        jobDescription: defaultDescription,
        fromWorkingHours: resolvedFromHours,
        fromWorkingMinutes: String(campaignData.fromWorkingMinutes ?? "0"),
        toWorkingHours: resolvedToHours,
        toWorkingMinutes: String(campaignData.toWorkingMinutes ?? "0"),
      },
      workingLocation,
    },
    organization: cp.organization || {
      name: prefill.companyProfile?.name,
      companyLogo: prefill.companyProfile?.logo,
    },
  };
}
